<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import PageLayout from 'dashboard/components-next/pilot/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/pilot/pageComponents/settings/SettingsHeader.vue';
import SuggestedScenarios from 'dashboard/components-next/pilot/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/pilot/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/pilot/assistant/AddNewScenariosDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('pilotScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const storeScenarios = useMapGetter('pilotScenarios/getRecords');

const searchQuery = ref('');

// 直接使用 store 数据
const scenarios = computed(() => storeScenarios.value || []);

// Suggested example scenarios for quick add - 简洁版建议数据
const scenariosExample = [
  {
    id: 1,
    title: '试用申请收集',
    description:
      '当用户想试用/演示产品、咨询价格版本、询问产品能力、表达购买意向、有定制/私有化需求时触发',
    instruction: `你是一个专业的售前顾问，负责帮助用户申请产品试用。

## 你的任务
1. 热情回应用户需求
2. 简要介绍产品优势（1-2句话）
3. 询问客户的申请方式：个人申请 还是 企业申请
4. 根据申请类型收集必要信息
5. 收集完整后调用 [更新联系人] 保存

## 信息收集规则

【个人申请】必须收集：姓名、手机、需求

【企业申请】必须收集：公司、姓名、手机、需求、行业

【可选】了解途径、职位

## 严格规则

1. 必须收集完所有【必须收集】字段后，才能调用工具
2. 用户只提供部分信息时，继续询问直到收集完整
3. 禁止在信息收集完整前转人工

## 注意

- 不直接报价或承诺价格
- 引导用户留下联系方式
- 语气热情专业
- 每次只问1-2个问题`,
    tools: ['update_contact'],
  },
  {
    id: 2,
    title: '潜在客户跟进',
    description: '处理有兴趣购买产品或服务的客户',
    instruction:
      '当客户表示有购买意向时，收集以下信息：许可证数量、是否从其他平台迁移。然后记录信息并添加"销售"标签，转交给销售人员跟进。',
    tools: ['add_private_note', 'add_label_to_conversation', 'handoff'],
  },
  {
    id: 3,
    title: '退款请求处理',
    description: '处理客户的退款申请流程',
    instruction:
      '确认订单号和购买日期，询问退款原因，检查是否符合退款政策（购买后7天内）。符合条件则记录信息并添加"退款处理中"标签。',
    tools: ['add_private_note', 'add_label_to_conversation'],
  },
  {
    id: 4,
    title: '技术问题排查',
    description: '处理产品使用中的技术问题',
    instruction:
      '先了解问题现象和复现步骤，查看知识库是否有相关解决方案。如无法解决，收集设备信息、版本号等详细信息后转交技术支持。',
    tools: ['add_private_note', 'handoff'],
  },
  {
    id: 5,
    title: '账户问题处理',
    description: '帮助用户解决账户登录、权限等问题',
    instruction:
      '核实用户身份后，协助处理密码重置、账户解绑等问题。涉及敏感操作时需验证用户身份，必要时转交客服处理。',
    tools: ['add_private_note', 'add_label_to_conversation'],
  },
  {
    id: 6,
    title: '投诉处理',
    description: '处理客户投诉和不满情绪',
    instruction:
      '耐心倾听客户诉求，表达理解和歉意。记录投诉内容，承诺跟进处理。添加"投诉"标签，转交客服主管处理。',
    tools: ['add_private_note', 'add_label_to_conversation', 'handoff'],
  },
];

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  // 始终显示建议数据（开发测试用）
  return true;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('PILOT.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('PILOT.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('PILOT.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

// 更新场景
const updateScenario = async scenario => {
  try {
    await store.dispatch('pilotScenarios/update', {
      id: scenario.id,
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('PILOT.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      error?.message ||
      t('PILOT.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

// 删除场景
const deleteScenario = async id => {
  try {
    await store.dispatch('pilotScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert(t('PILOT.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      error?.message ||
      t('PILOT.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// 批量删除场景
const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  try {
    await Promise.all(
      idsArray.map(id =>
        store.dispatch('pilotScenarios/delete', {
          id,
          assistantId: assistantId.value,
        })
      )
    );
    bulkSelectedIds.value = new Set();
    useAlert(t('PILOT.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      error?.message ||
      t('PILOT.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// 添加场景
const addScenario = async scenario => {
  try {
    await store.dispatch('pilotScenarios/create', {
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('PILOT.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      error?.message ||
      t('PILOT.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

// 添加所有示例场景
const addAllExampleScenarios = async () => {
  try {
    for (const scenario of scenariosExample) {
      await store.dispatch('pilotScenarios/create', {
        assistantId: assistantId.value,
        ...scenario,
      });
    }
    useAlert(t('PILOT.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      error?.message ||
      t('PILOT.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store
    .dispatch('pilotScenarios/get', {
      assistantId: assistantId.value,
    })
    .catch(() => {});
  store.dispatch('pilotTools/getTools').catch(() => {});
});
</script>

<template>
  <PageLayout
    :header-title="$t('PILOT.ASSISTANTS.SCENARIOS.TITLE')"
    :is-fetching="false"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <SettingsHeader
        :heading="$t('PILOT.ASSISTANTS.SCENARIOS.TITLE')"
        :description="$t('PILOT.ASSISTANTS.SCENARIOS.DESCRIPTION')"
      />
      <!-- 推荐场景提示 -->
      <div v-if="shouldShowSuggestedRules" class="flex mt-7 flex-col gap-4">
        <SuggestedScenarios
          :title="$t('PILOT.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
          :items="scenariosExample"
          @close="closeSuggestedRules"
          @add="addAllExampleScenarios"
        >
          <template #default="{ item }">
            <div class="flex items-start justify-between w-full gap-3">
              <div class="flex flex-col min-w-0">
                <span class="text-sm text-n-slate-12">
                  {{ item.title }}
                </span>
                <span class="text-xs text-n-slate-11 mt-0.5 truncate">
                  {{ item.description }}
                </span>
              </div>
              <Button
                :label="
                  $t('PILOT.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE')
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                @click="addScenario(item)"
              />
            </div>
          </template>
        </SuggestedScenarios>
      </div>
      <!-- 场景列表区域 -->
      <div class="flex mt-7 flex-col gap-4">
        <div class="flex justify-between items-center">
          <BulkSelectBar
            v-model="bulkSelectedIds"
            :all-items="scenarios"
            :select-all-label="buildSelectedCountLabel"
            :selected-count-label="selectedCountLabel"
            :delete-label="
              $t('PILOT.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
            "
            @bulk-delete="bulkDeleteScenarios"
          >
            <template #default-actions>
              <AddNewScenariosDialog @add="addScenario" />
            </template>
          </BulkSelectBar>
          <div
            v-if="scenarios.length && bulkSelectedIds.size === 0"
            class="max-w-[22.5rem] w-full min-w-0"
          >
            <Input
              v-model="searchQuery"
              :placeholder="
                t('PILOT.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <!-- 空状态提示 -->
        <div
          v-if="scenarios.length === 0 && !shouldShowSuggestedRules"
          class="mt-1 mb-2"
        >
          <span class="text-n-slate-11 text-sm">
            {{ t('PILOT.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <!-- 搜索无结果 -->
        <div
          v-else-if="filteredScenarios.length === 0 && scenarios.length > 0"
          class="mt-1 mb-2"
        >
          <span class="text-n-slate-11 text-sm">
            {{ t('PILOT.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <!-- 场景列表 -->
        <div
          v-else-if="filteredScenarios.length > 0"
          class="flex flex-col gap-2"
        >
          <ScenariosCard
            v-for="scenario in filteredScenarios"
            :id="scenario.id"
            :key="scenario.id"
            :title="scenario.title"
            :description="scenario.description"
            :instruction="scenario.instruction"
            :tools="scenario.tools"
            :is-selected="bulkSelectedIds.has(scenario.id)"
            :selectable="
              hoveredCard === scenario.id || bulkSelectedIds.size > 0
            "
            @select="handleRuleSelect"
            @delete="deleteScenario(scenario.id)"
            @update="updateScenario"
            @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
